import React, { useEffect, useState } from 'react';
import { adminAPI } from '../services/api';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Download, Search, Users, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const AdminLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const load = () => {
    setLoading(true);
    adminAPI
      .listLeads()
      .then(setLeads)
      .catch(() => toast.error('Failed to load leads'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = leads.filter((l) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (l.first_name || '').toLowerCase().includes(q) ||
      (l.phone || '').includes(q)
    );
  });

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const blob = await adminAPI.exportLeads();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redpanda-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download CSV');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-400" /> Guest leads
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Every visitor who verified their phone number to enter the site.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={load}
              className="border-gray-600 text-gray-200 hover:bg-gray-700"
              data-testid="leads-refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={downloadCsv}
              disabled={exporting || leads.length === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="leads-download-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Preparing...' : 'Download CSV'}
            </Button>
          </div>
        </div>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-5 space-y-4">
            <div className="relative max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input
                placeholder="Search name or phone..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-gray-900 border-gray-700 text-white"
                data-testid="leads-search-input"
              />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-700">
                    <th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4">Mobile</th>
                    <th className="py-3 pr-4">First verified</th>
                    <th className="py-3 pr-4">Last seen</th>
                    <th className="py-3">Verifications</th>
                  </tr>
                </thead>
                <tbody className="text-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="py-10 text-center text-gray-400">
                        Loading leads...
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="py-10 text-center text-gray-400">
                        {leads.length === 0
                          ? 'No leads yet. As guests verify their phone number, they will appear here.'
                          : 'No leads match your search.'}
                      </td>
                    </tr>
                  ) : (
                    filtered.map((l) => (
                      <tr
                        key={l.phone + l.verified_at}
                        className="border-b border-gray-800 hover:bg-gray-800/50"
                        data-testid={`lead-row-${l.phone}`}
                      >
                        <td className="py-3 pr-4 font-medium">{l.first_name}</td>
                        <td className="py-3 pr-4 font-mono">{l.phone}</td>
                        <td className="py-3 pr-4 text-gray-400">{formatDate(l.verified_at)}</td>
                        <td className="py-3 pr-4 text-gray-400">{formatDate(l.last_seen_at)}</td>
                        <td className="py-3 text-gray-300">{l.verifications_count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {!loading && filtered.length > 0 && (
              <p className="text-xs text-gray-500">
                Showing {filtered.length} of {leads.length} lead{leads.length === 1 ? '' : 's'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminLeads;
